// "Measured edges" (phase SC5): upload the gap edges extracted from a measured map and draw them as dots on the
// computed map. The edges are kept in this browser for the project (not on the server); fitting comes later.
import { Alert, Button, FileButton, Group, NumberInput, Select, Stack, Text } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { Plot } from "../../components/Plot";
import { edgesCsv, guessPanel, overlayFigure, parseEdges, type EdgeSet } from "../../lib/scEdges";

const key = (projectId: string | null | undefined) => `prayog.edges.${projectId ?? "none"}`;

function load(projectId: string | null | undefined): { set: EdgeSet; name: string } | null {
  try {
    const t = localStorage.getItem(key(projectId));
    return t ? JSON.parse(t) : null;
  } catch {
    return null;
  }
}

function save(projectId: string | null | undefined, v: { set: EdgeSet; name: string } | null) {
  try {
    if (v) localStorage.setItem(key(projectId), JSON.stringify(v));
    else localStorage.removeItem(key(projectId));
  } catch {
    /* storage unavailable: keep it for this view only */
  }
}

function readText(file: File): Promise<string> {
  if (typeof file.text === "function") return file.text();
  return new Promise((ok, fail) => {
    const fr = new FileReader();
    fr.onload = () => ok(String(fr.result ?? ""));
    fr.onerror = () => fail(fr.error);
    fr.readAsText(file);
  });
}

export function EdgesOverlay({ r, projectId }: { r: Record<string, any>; projectId?: string | null }) {
  const [held, setHeld] = useState(() => load(projectId));
  const [error, setError] = useState("");
  const [pick, setPick] = useState<number>(() => (held ? guessPanel(r, held.set) : 0));
  const panel = held?.set.panels[Math.min(pick, (held?.set.panels.length ?? 1) - 1)] ?? null;
  const [offset, setOffset] = useState<number>(panel?.bias_offset_mV ?? 0);
  const [xShift, setXShift] = useState(0);
  const [xScale, setXScale] = useState(1);
  useEffect(() => setOffset(panel?.bias_offset_mV ?? 0), [panel]);
  const open = (file: File | null) => {
    if (!file) return;
    readText(file).then((t) => {
      try {
        const set = parseEdges(t);
        const v = { set, name: file.name };
        setHeld(v);
        save(projectId, v);
        setPick(guessPanel(r, set));
        setError("");
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    });
  };
  const fig = useMemo(() => overlayFigure(r, panel, { xShift, xScale, offset }), [r, panel, xShift, xScale, offset]);
  const download = () => {
    if (!held) return;
    const url = URL.createObjectURL(new Blob([edgesCsv(held.set)], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = held.name.replace(/\.[^.]+$/, "") + "_edges.csv";
    a.click();
    URL.revokeObjectURL(url);
  };
  const gate = r.mode === "gate" || Array.isArray(r.Vg);
  return (
    <Stack gap="xs" data-testid="edges-overlay">
      <Group gap="xs" align="flex-end">
        <FileButton onChange={open} accept=".json,.csv,.txt,.tsv">{(props) => <Button size="xs" variant="light" {...props}>Upload measured edges…</Button>}</FileButton>
        {held && <>
          <Select size="xs" w={220} label="Panel" allowDeselect={false} value={String(pick)} onChange={(v) => setPick(Number(v ?? 0))}
            data={held.set.panels.map((p, i) => ({ value: String(i), label: `${p.name} (${p.X.length} points)` }))} data-testid="edges-panel" />
          <NumberInput size="xs" w={150} label="bias offset" rightSection={<Text size="xs" c="dimmed">mV</Text>} rightSectionWidth={36}
            step={0.01} decimalScale={4} value={offset} onChange={(v) => setOffset(typeof v === "number" ? v : Number(v) || 0)} data-testid="edges-offset" />
          <NumberInput size="xs" w={130} label="X shift" step={0.1} decimalScale={6} value={xShift} onChange={(v) => setXShift(typeof v === "number" ? v : Number(v) || 0)} />
          <NumberInput size="xs" w={110} label="X scale" step={0.1} decimalScale={6} value={xScale} onChange={(v) => setXScale(typeof v === "number" ? v : Number(v) || 1)} />
          <Button size="xs" variant="subtle" onClick={download}>CSV</Button>
          <Button size="xs" variant="subtle" color="gray" onClick={() => { setHeld(null); save(projectId, null); }}>Remove</Button>
        </>}
      </Group>
      {held && <Text size="xs" c="dimmed">{held.name}: {held.set.panels.length} panel{held.set.panels.length === 1 ? "" : "s"}</Text>}
      {error && <Alert color="red" p="xs">{error}</Alert>}
      <Plot data={fig.data} layout={fig.layout} height={460} />
      <Text size="xs" c="dimmed">The upper and lower edges of the measured zero-conductance region, as JSON
        {" {\"panels\": {\"Bz\": {\"X\": […], \"upper\": […], \"lower\": […], \"bias_offset_mV\": 0.08}, …}} "}or a CSV with the columns
        panel, X, upper, lower (or two columns X, V). X is in the units of this map ({gate ? "gate V_g in mV" : r.unit ?? r.variable}); X shift and
        X scale map the measured X onto it (plotted at scale × (X − shift), e.g. a top-gate voltage in V to mV from a degeneracy). The bias offset
        (measured bias minus the true one) is taken off the measured edges. The file stays in this browser, for this project; fitting the model
        to the edges is the next phase.</Text>
    </Stack>
  );
}
